/**
 * Feature Flag Context
 * 
 * Provides feature flag functionality throughout the application.
 * Supports both local development flags and production API-based flags.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { useAuth } from '@/hooks/useAuth';

interface FeatureFlag {
  key: string;
  enabled: boolean;
  description?: string;
}

interface UserFeatureFlags {
  user_email: string;
  flags: Array<{
    key: string;
    is_enabled: boolean;
  }>;
}

interface FeatureFlagContextType {
  isFeatureEnabled: (flagKey: string) => boolean;
  isLoading: boolean;
  error: string | null;
  refreshFlags: () => Promise<void>;
  getUserSpecificFlags: () => Record<string, boolean>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

// Local development feature flags - these are always enabled in development
const LOCAL_DEVELOPMENT_FLAGS: Record<string, boolean> = {
  'multi_agent_bot': true, // Always show the bot in development
  'customer_ai_chat': true,
  'advanced_analytics': true,
  // Add more development-only features here
};

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [userSpecificFlags, setUserSpecificFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();

  const isDevelopment = process.env.NODE_ENV === 'development';

  const fetchFlags = async () => {
    // In development, use local flags
    if (isDevelopment) {
      setFlags(LOCAL_DEVELOPMENT_FLAGS);
      setUserSpecificFlags(LOCAL_DEVELOPMENT_FLAGS);
      setIsLoading(false);
      return;
    }

    // In production, fetch from API
    try {
      setIsLoading(true);
      setError(null);
      
      // Use the new public endpoint that returns user-specific effective flags
      const response = await ApiService.request<UserFeatureFlags>(
        EndpointKeys.GET_PUBLIC_FEATURE_FLAGS,
        { method: 'GET' }
      );

      console.log('Public Feature Flags Debug:', {
        response,
        userEmail: user?.email,
        isAuthenticated
      });

      // Convert flags array to object for easier lookup
      const flagsObject = response.flags.reduce((acc, flag) => {
        acc[flag.key] = flag.is_enabled;
        return acc;
      }, {} as Record<string, boolean>);

      setFlags(flagsObject);
      setUserSpecificFlags(flagsObject);
      console.log('Using public feature flags:', flagsObject);

    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError('Failed to load feature flags');
      // Fallback to empty flags object (all features disabled)
      setFlags({});
      setUserSpecificFlags({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, [isDevelopment, isAuthenticated, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFeatureEnabled = (flagKey: string): boolean => {
    // In development, always check local flags first
    if (isDevelopment && LOCAL_DEVELOPMENT_FLAGS.hasOwnProperty(flagKey)) {
      return LOCAL_DEVELOPMENT_FLAGS[flagKey];
    }

    // For authenticated users, check user-specific flags first
    if (isAuthenticated && userSpecificFlags.hasOwnProperty(flagKey)) {
      return userSpecificFlags[flagKey] === true;
    }

    // Otherwise check the global flags
    return flags[flagKey] === true;
  };

  const getUserSpecificFlags = (): Record<string, boolean> => {
    if (isDevelopment) {
      return LOCAL_DEVELOPMENT_FLAGS;
    }
    return isAuthenticated ? userSpecificFlags : flags;
  };

  const refreshFlags = async () => {
    await fetchFlags();
  };

  const contextValue: FeatureFlagContextType = {
    isFeatureEnabled,
    isLoading,
    error,
    refreshFlags,
    getUserSpecificFlags,
  };

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
  }
  return context;
};

// Convenience hook for checking a single feature flag
export const useFeatureEnabled = (flagKey: string): boolean => {
  const { isFeatureEnabled } = useFeatureFlag();
  return isFeatureEnabled(flagKey);
};

export default FeatureFlagContext;
